import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { exportIncomeToPDF } from "../../utils/exportIncome";

import { formatJalaliDate } from "../utils/dateFormat";
import { JalaliDatePickerField } from "../Components/JalaliDateField";
export default function IncomeTable({ selectedBranch = "" }) {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  const allAccountsOption = React.useMemo(
    () => ({ id: "", name: t("ReportIndex.all_accounts", "All Accounts") }),
    [t]
  );

  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [selectedAccount, setSelectedAccount] = React.useState("");
  const [accountSearchTerm, setAccountSearchTerm] = React.useState("");
  const [selectedAccountOption, setSelectedAccountOption] = React.useState(
    allAccountsOption
  );
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  React.useEffect(() => {
    setPage(1);
  }, [startDate, endDate, selectedAccount, selectedBranch]);

  React.useEffect(() => {
    if (!selectedAccount && !accountSearchTerm.trim()) {
      setSelectedAccountOption(allAccountsOption);
    }
  }, [allAccountsOption, accountSearchTerm, selectedAccount]);

  const queryString = React.useMemo(() => {
    const params = [];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedBranch) params.push(`shop=${selectedBranch}`);
    if (selectedAccount) params.push(`account=${selectedAccount}`);
    if (rowsPerPage === -1) {
      params.push("page=1");
      params.push("page_size=10000");
    } else {
      params.push(`page=${page}`);
      params.push(`page_size=${rowsPerPage}`);
    }
    return params.join("&");
  }, [endDate, page, rowsPerPage, selectedAccount, selectedBranch, startDate]);

  const { data: incomesData, isLoading, isError } = useFetchObjects(
    ["income-report", queryString],
    `shop/incomes/?${queryString}`,
    token
  );

  const accountQuery = React.useMemo(() => {
    const params = [];
    if (selectedBranch) params.push(`shop=${selectedBranch}`);
    if (accountSearchTerm.trim()) {
      params.push(`search=${encodeURIComponent(accountSearchTerm.trim())}`);
    }
    params.push("page_size=20");
    return params.join("&");
  }, [accountSearchTerm, selectedBranch]);

  const { data: accountsData, isLoading: accountsLoading } = useFetchObjects(
    ["income-report-accounts", accountQuery],
    `shop/accounts/?${accountQuery}`,
    token
  );

  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];

  const accountOptions = React.useMemo(() => {
    const options = [allAccountsOption, ...accounts];
    if (!selectedAccountOption) return options;

    const exists = options.some(
      (account) =>
        String(account?.id ?? "") === String(selectedAccountOption?.id ?? "")
    );

    return exists ? options : [selectedAccountOption, ...options];
  }, [accounts, allAccountsOption, selectedAccountOption]);

  const incomes = incomesData?.results || incomesData || [];
  const totalCount = incomesData?.count || incomes.length;

  const summaryByCurrency = React.useMemo(() => {
    return incomes.reduce((acc, income) => {
      const currency = income?.currency_details?.title || "---";
      const amount = Number(income?.amount) || 0;
      acc[currency] = (acc[currency] || 0) + amount;
      return acc;
    }, {});
  }, [incomes]);

  const open = Boolean(anchorEl);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `income_${today}`;

    if (type === "pdf") {
      exportIncomeToPDF(incomes, `${base}.pdf`);
    }

    setAnchorEl(null);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
          alignItems: "center",
          gap: 1,
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
        <Typography>{t("ReportIndex.loading")}</Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
          flexDirection: "column",
          alignItems: "center",
          px: 2,
          gap: 1,
        }}
      >
        <Typography variant="h6" color="error">
          {t("ReportIndex.error")}{" "}
          <RefreshIcon sx={{ cursor: "pointer", verticalAlign: "middle" }} />
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <JalaliDatePickerField
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <JalaliDatePickerField
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <Autocomplete
                  fullWidth
                  options={accountOptions}
                  value={selectedAccountOption}
                  inputValue={accountSearchTerm}
                  onChange={(event, newValue) => {
                    const nextOption =
                      newValue && String(newValue.id ?? "") === ""
                        ? allAccountsOption
                        : newValue || allAccountsOption;

                    setSelectedAccountOption(nextOption);
                    setSelectedAccount(nextOption?.id ? String(nextOption.id) : "");
                    setAccountSearchTerm(nextOption?.name || "");
                    setPage(1);
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      setAccountSearchTerm(newInputValue);
                      if (
                        selectedAccount ||
                        selectedAccountOption?.id === "" ||
                        selectedAccountOption?.name !== newInputValue
                      ) {
                        setSelectedAccount("");
                        setSelectedAccountOption(null);
                        setPage(1);
                      }
                    }

                    if (reason === "clear") {
                      setAccountSearchTerm("");
                      setSelectedAccount("");
                      setSelectedAccountOption(allAccountsOption);
                      setPage(1);
                    }
                  }}
                  loading={accountsLoading}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id ?? "") === String(value?.id ?? "")
                  }
                  clearOnBlur={false}
                  openOnFocus
                  autoHighlight
                  noOptionsText={t("ReportIndex.no_accounts", "No accounts found")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("ReportIndex.income_table_account", "Account")}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedAccount("");
                    setAccountSearchTerm("");
                    setSelectedAccountOption(allAccountsOption);
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    MenuListProps={{
                      "aria-labelledby": "export-button",
                    }}
                  >
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => setAnchorEl(null)}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, ml: 2 }}>
        <Typography>{t("reports.rowsPerPage")}</Typography>
        <FormControl size="small">
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={30}>30</MenuItem>
            <MenuItem value={-1}>{t("reports.all")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider />

      <TableContainer component={Paper}>
        <Table aria-label={t("ReportIndex.income_tableTitle")}>
          <TableHead>
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography variant="h4">
                  {t("ReportIndex.income_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("ReportIndex.income_table_id")}</TableCell>
              <TableCell>{t("ReportIndex.income_table_branch")}</TableCell>
              <TableCell>{t("ReportIndex.income_table_account")}</TableCell>
              <TableCell>{t("ReportIndex.income_table_amount")}</TableCell>
              <TableCell>{t("ReportIndex.income_table_exchangeRate")}</TableCell>
              <TableCell>{t("ReportIndex.income_table_convertedAmount")}</TableCell>
              <TableCell>{t("ReportIndex.income_table_paymentDate")}</TableCell>
              <TableCell>{t("ReportIndex.income_table_notes")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {incomes.map((income) => (
              <TableRow key={income.id}>
                <TableCell>{income.id}</TableCell>
                <TableCell>{income.shop_name || "-"}</TableCell>
                <TableCell>{income.account_details?.name || "-"}</TableCell>
                <TableCell>
                  {income.amount || "0"} {income.currency_details?.title || "-"}
                </TableCell>
                <TableCell>{income.exchange_rate || "-"}</TableCell>
                <TableCell>{income.converted_amount || "-"}</TableCell>
                <TableCell>
                  {income.income_date
                    ? formatJalaliDate(income.income_date)
                    : "-"}
                </TableCell>
                <TableCell>{income.notes || "-"}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: "bold" }}>{t("Summary")}</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {Object.entries(summaryByCurrency)
                  .map(([currency, amount]) => `${amount.toFixed(2)} ${currency}`)
                  .join(", ") || "0.00"}
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {rowsPerPage !== -1 && (
        <Grid container justifyContent="center" alignItems="center" padding="10px">
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page}
            onChange={(event, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
          />
        </Grid>
      )}
    </>
  );
}
